// noinspection SpellCheckingInspection
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import GuidSchema, { EMPTY_GUID_VALUE, GuidParams } from "_shared/guids/guid-schema";
import { v4 as uuidv4 } from "uuid";
import { IRedlineableField } from "work/proposals/redlining/field-redlines/field-redline";

export default class Guid implements IRedlineableField {
  public static get EMPTY(): Guid {
    return new Guid(EMPTY_GUID_VALUE, { allowEmpty: true });
  }

  public static generate(): Guid {
    // https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID
    return new Guid(uuidv4());
  }

  public static isEmpty(value?: string): boolean {
    return !value || value === EMPTY_GUID_VALUE;
  }

  public static create(value: GuidParams) {
    return new Guid(value);
  }

  public static fromJSON(data: unknown, options?: { allowEmpty: boolean }): Guid | null | undefined {
    options ??= { allowEmpty: false };
    if (isNullOrUndefined(data)) return data;
    const parsed = GuidSchema.deserialize(data, options.allowEmpty);
    return new Guid(parsed, options);
  }

  public static fromJSONArray(jsonArray: unknown, options?: { allowEmpty: boolean }): Guid[] | null | undefined {
    options ??= { allowEmpty: false };
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    const parsedArray = GuidSchema.deserializeArray(jsonArray, options.allowEmpty);
    return parsedArray.map((parsed) => new Guid(parsed, options));
  }

  public static isValid(value?: string, allowEmpty: boolean = false): boolean {
    try {
      GuidSchema.validate(value, allowEmpty);
      return true;
    } catch {
      return false;
    }
  }

  public get isEmpty(): boolean {
    return Guid.isEmpty(this.value);
  }

  public constructor(public readonly value: string, options?: { allowEmpty: boolean }) {
    const validated = GuidSchema.validate(value, options?.allowEmpty);
    this.value = validated;
  }

  public toJSON(): string {
    return this.value;
  }

  public toString(): string {
    return this.value;
  }

  public valueOf(): string {
    return this.value;
  }

  public isEqualTo(other: Guid | null | undefined): boolean {
    return other?.value != undefined && this.value === other.value;
  }

  public clone(): this {
    return this;
  }
}
